import React, { useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { financialDetailsData } from '../../../referenceData/financialDetailsData/financialDetailsData';
import {
  EMPLOYMENT_STATUS_WORKING_OPTIONS,
  SHOW_EMPLOYMENT_ROLE,
  CHINA,
  MAX_COMPANY_NAME_CHAR,
  NOT_TIN_REASON_OPTIONS,
  SELF_EMPLOYED,
  SHOW_EMPLOYMENT_INDUSTRY
} from '../../../referenceData/constants';
import { Country } from 'referenceData';
import { useHistory } from 'react-router-dom';
import { getUserSessionDataByKey, setUserSessionDataByKey } from 'storage/userStorage';
import {
  Section,
  Heading,
  Expander,
  Paragraph,
  IconStatusWarningOnlight,
  IconChevronDown,
  Reveal,
  HorizontalRule,
  Link,
  FormLayout,
  Button,
  ModalDialog,
  ButtonRow,
} from '@piggybank/core';
import i18n from 'i18next';
import {
  Form,
  FormFeedback,
  Field,
  Fieldset,
  YesNoRadio,
  Label,
  AddAnother,
  Select,
  TextInput,
  TextArea,
  FieldFeedback,
  Checkbox,
  CurrencyInput,
  Radio
} from '@piggybank/form';
import { WithI18nProgressIndicator } from 'components/WithI18nPiggybank';
import { ButtonWithBack } from 'components/ButtonWithBack';
import './financialDetails.css';
import { rePrevious } from 'utils/util';
import { getAllValue } from 'utils/customerNameFormat';
import { getTaxValidationErrors, globalCheckTaxValidations, hasTaxValidationErrors } from 'utils/globalCheckTaxValidations';
import { validationTagging, useTagging, linkTagging, loadTagging, globalViewTaggingParams } from 'tealiumTrack';

const CHARACTERS_AT_MOST = 'ICO.PERSONAL.CHARACTERS_AT_MOST';
const FINANCIAL_SELECTCOUNTRY = 'ICO.FINANCIAL.SELECTCOUNTRY';
const TAG_PAGE_URL = '/forms/financial-details';
const TAG_PAGE_NAME = 'pws:forms:financial details';
const COMMON_SELECT = 'ICO.COMMON.SELECT';
const COMMON_SPECIAL_ERROR = 'ICO.COMMON.SPECIAL_ERROR';
const OTHER_REASON_VALUE = 'E';
const WAITING_FOR_TIN_REASON_VALUE = 'EXPLANATION1';
const WAITING_FOR_TIN_REMINDER = 'ICO.FINANCIAL.dico_journey_tax_resident_none_tin_reason_reminder';
const OECD_TAX_RESIDENCY_URL = 'https://www.oecd.org/en/networks/global-forum-tax-transparency/resources/aeoi-implementation-portal/tax-residency.html';
const MODAL_REASON_MAX_LENGTH = 130;

export const hasValidationErrors = (errors) => Object.keys(errors || {}).length > 0;

export const getTaxResidencyCountryOptions = (countries = [], additionalNationalities = [], index) => {
  const selectedCountries = additionalNationalities
    .filter((_, countryIndex) => countryIndex !== index)
    .map(({ countryOfTaxResidency }) => countryOfTaxResidency)
    .filter(Boolean);

  return countries.filter(({ value }) => !selectedCountries.includes(value));
};

export const getNoTinReasonDisplayWarning = ({ selectedReasonForNoTIN, t }) => (
  selectedReasonForNoTIN === WAITING_FOR_TIN_REASON_VALUE
    ? t(WAITING_FOR_TIN_REMINDER)
    : ''
);

export const getNoTinReasonDisplayError = ({ reasonForNoTINError }) => reasonForNoTINError || '';

const renderTinHelpText = (t, id) => (
  <div className="financialTinHelp">
    <Paragraph id={id} hint>
      {t('ICO.FINANCIAL.EXAMPLE_TIN_HELP_PREFIX')}
      <Link
        buttonPadding={false}
        elementType="a"
        external
        href={OECD_TAX_RESIDENCY_URL}
        strong={false}
        textMap={{
          newWindow: ''
        }}
        className="financialTinHelpLink"
      >
        {t('ICO.FINANCIAL.EXAMPLE_TIN_HELP_LINK')}
      </Link>
    </Paragraph>
  </div>
);

const validateModalReason = (value, t) => {
  if (!value?.trim()) {
    return t('ICO.FINANCIAL.MODAL_REASON_ERROR_MESSAGE');
  }

  if (value.length > MODAL_REASON_MAX_LENGTH) {
    return t('ICO.FINANCIAL.MODAL_REASON_MAX_LENGTH_ERROR');
  }

  return '';
};

const WarningFeedback = ({ children }) => (
  <div className="financialWarningFeedback" data-testid="financial-warning-feedback">
    <span className="financialWarningFeedback__icon">
      <IconStatusWarningOnlight marginBottom={0} />
    </span>
    <span className="financialWarningFeedback__message">{children}</span>
  </div>
);

const getTaxValidationFieldError = (taxValidationErrorsRef, field, context, value) => {
  const index = Number(context.path.match(/additionalNationalities\[(\d+)\]/)?.[1]);
  const submittedItem = taxValidationErrorsRef.current?.values?.additionalNationalities?.[index];
  const error = taxValidationErrorsRef.current?.errors?.additionalNationalities?.[index]?.[field];

  return submittedItem?.countryOfTaxResidency === context.parent.countryOfTaxResidency &&
    submittedItem?.agree === context.parent.agree &&
    submittedItem?.[field] === value ? error : undefined;
};

const createSchema = (taxValidationErrorsRef) => yup.object().shape({
  employmentStatus: yup.string().required(() => i18n.t('ICO.FINANCIAL.SELEMPLOYMENTSTATUS')),
  employmentRole: yup.string().when('employmentStatus', {
    is: (val) => SHOW_EMPLOYMENT_ROLE.includes(val),
    then: yup.string().required(() => i18n.t('ICO.FINANCIAL.SELEMPLOYMENTROLE')),
    otherwise: yup.string(),
  }),
  occupation: yup.string().when('employmentStatus', {
    is: (val) => EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(val),
    then: yup.string().required(() => i18n.t('ICO.FINANCIAL.SELECTOCCUPATION')),
    otherwise: yup.string(),
  }),
  industry: yup.string().when('employmentStatus', {
    is: (val) => SHOW_EMPLOYMENT_INDUSTRY.includes(val),
    then: yup.string().required(() => i18n.t('ICO.FINANCIAL.SELINDUSTRY')),
    otherwise: yup.string(),
  }),
  employerName: yup.string().when('employmentStatus', {
    is: (val) => EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(val),
    then: yup.string()
      .max(MAX_COMPANY_NAME_CHAR, () => i18n.t('ICO.FINANCIAL.EMPLOYERS_NAME_MAX_LIMIT_ERROR'))
      .required(() => i18n.t('ICO.FINANCIAL.SELEMPLOYERSNAME'))
      .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(COMMON_SPECIAL_ERROR)),
    otherwise: yup.string(),
  }),
  addressCountry: yup.string().when('employmentStatus', {
    is: (val) => EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(val),
    then: yup.string().required(() => i18n.t(FINANCIAL_SELECTCOUNTRY)),
    otherwise: yup.string(),
  }),
  gross: yup.string().when('employmentStatus', {
    is: (val) => EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(val),
    then: yup.string()
      .test('is-valid-gross', function (value) {
        if (!value) {
          return this.createError({
            message: this.parent?.employmentStatus === SELF_EMPLOYED
              ? i18n.t('ICO.FINANCIAL.AVERAGE_MONTHLY_ERROR')
              : i18n.t('ICO.FINANCIAL.SELHKD'),
          });
        }
        return true;
      }),
    otherwise: yup.string(),
  }),
  paidFromEmployerIndicator: yup.string().when('employmentStatus', {
    is: (val) => EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(val),
    then: yup.string()
      .matches(/(yes|no)/)
      .required(() => i18n.t('ICO.FINANCIAL.PAID_FORM_EMPLOYER_ERROR')),
    otherwise: yup.string(),
  }),
  additionalNationalities: yup.array().of(
    yup.object().shape({
      countryOfTaxResidency: yup.string().required(() => i18n.t(FINANCIAL_SELECTCOUNTRY)),
      taxDocumentIdNumber: yup.string()
        .when(['agree', 'countryOfTaxResidency'], {
          is: (agree, countryOfTaxResidency) => !agree || countryOfTaxResidency === CHINA,
          then: yup.string().max(20, ({ max }) => i18n.t(CHARACTERS_AT_MOST, { filedText: i18n.t('ICO.FINANCIAL.MAXTIN2'), max: max }))
            .required(() => i18n.t('ICO.FINANCIAL.SELENTERTAX'))
            .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(COMMON_SPECIAL_ERROR)),
          otherwise: yup.string(),
        })
        .test('tax-validation-tin', function (value) {
          const error = getTaxValidationFieldError(taxValidationErrorsRef, 'taxDocumentIdNumber', this, value);
          return error ? this.createError({ message: error }) : true;
        }),
      reasonForNoTIN: yup.string().when(['agree', 'countryOfTaxResidency'], {
        is: (agree, countryOfTaxResidency) => agree && countryOfTaxResidency !== CHINA,
        then: yup.string().required(() => i18n.t('ICO.FINANCIAL.TELL')),
        otherwise: yup.string(),
      }).test('tax-validation-reason', function (value) {
        const error = getTaxValidationFieldError(taxValidationErrorsRef, 'reasonForNoTIN', this, value);
        return error ? this.createError({ message: error }) : true;
      }),
      explainForNotEligibleApply: yup
        .string()
        .when("reasonForNoTIN", {
          is: (val) => val === 'EXPLANATION2',
          then: yup
            .string()
            .max(130, () => i18n.t('ICO.FINANCIAL.MODAL_REASON_MAX_LENGTH_ERROR'))
            .required(() => i18n.t('ICO.FINANCIAL.SELARRLY'))
            .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(COMMON_SPECIAL_ERROR))
        }),
    })
  ),
});

const FinancialDetails = () => {
  const history = useHistory();
  const taxValidationErrorsRef = useRef(null);
  const schema = useMemo(() => createSchema(taxValidationErrorsRef), []);
  const [showModal, setShowModal] = useState(false);
  const [showResidentialReason, setShowResidentialReason] = useState(false);
  const [showCorrespondenceReason, setShowCorrespondenceReason] = useState(false);
  const [isTaxValidationLoading, setIsTaxValidationLoading] = useState(false);
  const initialModalValues = { residentialReason: '', residentialReasonOther: '', correspondenceReason: '', explainResidenceReasonText: '' };
  const addressValues = getUserSessionDataByKey('address');
  const initialValues = getUserSessionDataByKey('financial');
  const { t } = useTranslation();
  const { employmentStatusList, selfEmployedRoleList, seniorManagerRoleList, occupation, residentialReasonList, correspondenceReasonList } = financialDetailsData[`${i18n.language}`];
  const countryList = Country[`${i18n.language}`];

  const goIndustry = (values) => {
    setUserSessionDataByKey('financial', values);
    history.push('/industryView');
  }

  const { isPremier, lang } = getUserSessionDataByKey('global');
  useTagging({
    'page_url': TAG_PAGE_URL,
    'page_language': lang,
    'page_name': TAG_PAGE_NAME,
    'page_subcategory': 'financial details',
    'raw_datalayer': '5745v14',
    'funnel_name': `account opening: ${isPremier ? 'premier' : 'one'}`,
    'funnel_step': '4',
    'funnel_step_name': 'financial details'
  });

  const taggingParams = {
    'page_url': TAG_PAGE_URL,
    'page_language': lang,
    'event_category': 'error',
    'event_action': 'field validation',
    'page_name': TAG_PAGE_NAME,
    'raw_datalayer': '5745v16'
  };

  function employmentStatusHandle({ next, value, rest }, { setFieldValue }) {
    setFieldValue('occupation', '');
    setFieldValue('employmentRole', '');
    setFieldValue('industry', '');
    setFieldValue('employerName', '');
    setFieldValue('addressCountry', '');
    setFieldValue('gross', '');
    setFieldValue('paidFromEmployerIndicator', '');

    linkTagging({
      'page_url': TAG_PAGE_URL,
      'page_language': lang,
      'event_category': 'content',
      'event_action': 'dropdown',
      'event_subcategory': 'employmentStatus',
      'event_content': `employment status: ${value}`,
      'page_name': TAG_PAGE_NAME,
      'raw_datalayer': '5745v15'
    });
    next({ value, ...rest });
  }

  function additionalNationalitiesAgreeHandle({ next, value, rest }, { values, index }){
    clearTaxValidationErrors(index);
    if (!value) {
      values.additionalNationalities[index].reasonForNoTIN = ''
      values.additionalNationalities[index].explainForNotEligibleApply = ''
    } else {
      values.additionalNationalities[index].taxDocumentIdNumber = ''
    }
    next({ value, ...rest });
  }

  function additionalNationalitiesReasonForNoTINHandle({ next, value, rest }, { values, index }){
    clearTaxValidationErrors(index, 'reasonForNoTIN');
    if (value !== 'EXPLANATION2') {
      values.additionalNationalities[index].explainForNotEligibleApply = ''
    }
    next({ value, ...rest });
  }

  const clearTaxValidationErrors = (index, field) => {
    const rowErrors = taxValidationErrorsRef.current?.errors?.additionalNationalities?.[index];

    if (rowErrors) {
      if (field) {
        delete rowErrors[field];
      } else {
        taxValidationErrorsRef.current.errors.additionalNationalities[index] = undefined;
      }
    }
  };

  const filterCountries = (countries = [], values = []) => {
    return countries.filter(country => country && values.includes(country.countryOfTaxResidency));
  }

  const validateCountryAddresses = (financialValues) => {
    const { additionalNationalities } = financialValues;
    const { correspondenceAddressDifferent, currentResidentialAddress, correspondenceAddress = {} } = addressValues;
    const residentialCountry = currentResidentialAddress.addressCountry;
    const correspondenceCountry = correspondenceAddress.addressCountry;
    const residentialCheck = filterCountries(additionalNationalities, [residentialCountry]).length > 0;

    if (correspondenceAddressDifferent === 'yes' ||
      (correspondenceAddressDifferent === 'no' && correspondenceCountry === residentialCountry)) {
      handleDifferentAddresses(residentialCheck, true);
    } else if (correspondenceAddressDifferent === 'no' && correspondenceCountry !== residentialCountry) {
      const correspondenceCheck = filterCountries(additionalNationalities, [correspondenceCountry]).length > 0;
      handleDifferentAddresses(residentialCheck, correspondenceCheck);
    }
  }
  const focusSelectDom = (formik = {}) => {
    if (formik.isSubmitting && !formik.isValidating) {
      let firstErrorInput = '';
      if (formik.errors.residentialReason) {
        firstErrorInput = document.querySelector('#residentialReason-field');
      } else if (formik.errors.residentialReasonOther) {
        firstErrorInput = document.querySelector('#residentialReasonOther-field');
      } else if (formik.errors.correspondenceReason) {
        firstErrorInput = document.querySelector('#correspondenceReason-field');
      } else if (formik.errors.explainResidenceReasonText) {
        firstErrorInput = document.querySelector('#explainResidenceReasonText-field');
      }
      if (firstErrorInput) {
        setTimeout(() => {
          if (typeof firstErrorInput.scrollIntoView === 'function') {
            firstErrorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          if (typeof firstErrorInput.focus === 'function') {
            firstErrorInput.focus();
          }
        }, 100);
      }
    }
  };

  const handleDifferentAddresses = (hideResidential, hideCorrespondence) => {
    if (hideResidential && hideCorrespondence) {
      history.push('/info/using');
    } else {
      setShowResidentialReason(!hideResidential);
      setShowCorrespondenceReason(!hideCorrespondence);
      setShowModal(true);

      // every time the modal is opened, trigger view event tagging
      loadTagging({ 
        ...globalViewTaggingParams, 
        ...getTaggingProperties(
          !hideResidential,
          !hideCorrespondence,
          'view',
          {
            page_type: 'form',
            page_subcategory: 'additional information popup',
            form_field_2: hideResidential ? 'Yes' : 'No',
          }
        )
      });
    }
  }  
  
  const rawDataLayerPrefix = `25515v`;
  const rawDataLayerSuffixes = {
    '/forms/additional-information-popup/why-current-address-is-different-from-tax-residency':
      {
        view: '1',
        dropdown1: '2',
        cancel: '3',
        submit: '4',
      },
    '/forms/additional-information-popup/why-mailing-address-is-different-from-tax-residency':
      {
        view: '5',
        dropdown2: '6',
        cancel: '7',
        submit: '8',
      },
    '/forms/additional-information-popup/why-current-and-mailing-address-is-different-from-tax-residency':
      {
        view: '9',
        dropdown1: '10',
        dropdown2: '11',
        cancel: '12',
        submit: '13',
      },
  };

  const getTaggingProperties = (
    showResidentialReason,
    showCorrespondenceReason,
    eventContent,
    otherPropObj = {}
  ) => {

    let pageUrl = '';
    let pageName = '';

    switch (true) {
      case showResidentialReason && !showCorrespondenceReason:
        pageUrl = `/forms/additional-information-popup/why-current-address-is-different-from-tax-residency`;
        pageName = `pws:forms:additional information popup:why current address is different from tax residency`;
        break;
      case !showResidentialReason && showCorrespondenceReason:
        pageUrl = `/forms/additional-information-popup/why-mailing-address-is-different-from-tax-residency`;
        pageName = `pws:forms:additional information popup:why mailing address is different from tax residency`;
        break;
      case showResidentialReason && showCorrespondenceReason:
        pageUrl = `/forms/additional-information-popup/why-current-and-mailing-address-is-different-from-tax-residency`;
        pageName = `pws:forms:additional information popup:why current and mailing address is different from tax residency`;
        break;
      default:
        pageUrl = `/forms/additional-information-popup/why-current-address-is-different-from-tax-residency`;
        pageName = `pws:forms:additional information popup:why current address is different from tax residency`;
        break;
    }

    const taggingProperties = {
      page_language: lang,
      event_category: 'content',
      page_url: pageUrl,
      page_name: pageName,
      raw_datalayer: `${rawDataLayerPrefix}${rawDataLayerSuffixes[pageUrl][eventContent]}`,
      ...otherPropObj,
    };

    return taggingProperties;
  };

  return (
    <>
      <FormLayout>
        <WithI18nProgressIndicator current={3} />
        <Section>
          <Heading level={1}>{t('ICO.FINANCIAL.DETAILS')}</Heading>
          <Paragraph lead>{t('ICO.FINANCIAL.NEXT')}</Paragraph>
          <HorizontalRule />
        </Section>
        <Form
          initialValues={initialValues}
          enableReinitialize={false}
          validationSchema={schema}
          onSubmit={async ({ values, formikBag }) => {
            setIsTaxValidationLoading(true);

            try {
              const taxValidationResult = await globalCheckTaxValidations(values);
              const taxValidationErrors = getTaxValidationErrors(values, taxValidationResult, t);

              if (hasTaxValidationErrors(taxValidationErrors)) {
                setIsTaxValidationLoading(false);
                taxValidationErrorsRef.current = {
                  values: {
                    additionalNationalities: values.additionalNationalities.map((item) => ({ ...item }))
                  },
                  errors: {
                    additionalNationalities: taxValidationErrors.additionalNationalities.map((item) => item && { ...item })
                  }
                };
                formikBag.setErrors(taxValidationErrors);
                return;
              }
              taxValidationErrorsRef.current = null;
            } catch (error) {
              setIsTaxValidationLoading(false);
              history.push('/tax-validation-technicals');
              return;
            }

            setIsTaxValidationLoading(false);
            setUserSessionDataByKey('financial', { ...values, ...initialModalValues });
            validateCountryAddresses(values);
          }}
          onChange={rePrevious}
        >

          {({ values, setFieldValue, isSubmitting, isValid, errors }) => {
            const { employmentStatus } = values;
            if (isSubmitting && !isValid && hasValidationErrors(errors)) {
              validationTagging({ errors, taggingParams });
            }
            return (
              <>
                <FormFeedback textMap={{ title: t('ICO.COMMON.CORRECT'), of: '/' }} />
                <Heading level={2} accentBar>
                  {t('ICO.FINANCIAL.EMPLOYMENT')}
                </Heading>
                <Field name="employmentStatus"
                  onChange={(next, { value, ...rest }) => employmentStatusHandle({ next, value, rest }, { setFieldValue })}
                >
                  <Label>{t('ICO.FINANCIAL.EMPLOYMENTSTATUS')}</Label>
                  <Select
                    options={employmentStatusList}
                    placeholder={t(COMMON_SELECT)}
                  />
                  <FieldFeedback />
                </Field>
                {EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(employmentStatus) &&
                  <>
                    {SHOW_EMPLOYMENT_ROLE.includes(employmentStatus) && <Field name="employmentRole"
                      onChange={(next, { value, ...rest }) => {
                        next({ value, ...rest });
                      }}
                    >
                      <Label>{t('ICO.FINANCIAL.EMPLOYMENTROLE')}</Label>
                      <Select
                        options={employmentStatus === SELF_EMPLOYED ? selfEmployedRoleList : seniorManagerRoleList}
                        placeholder={t(COMMON_SELECT)}
                      />
                      <FieldFeedback />
                    </Field>}
                    <Heading level={3} marginBottom={2}>{t('ICO.FINANCIAL.OCCUPATION_TITLE')}</Heading>
                    <Field name="occupation">
                      <Label>{t('ICO.FINANCIAL.OCCUPATION')}</Label>
                      <Select
                        options={occupation}
                        placeholder={t(COMMON_SELECT)}
                      />
                      <FieldFeedback />
                    </Field>
                    {SHOW_EMPLOYMENT_INDUSTRY.includes(employmentStatus) && <Field name="industry" onClick={() => goIndustry(values)} tabIndex="-1">
                      <Label>{t('ICO.FINANCIAL.INDUSTRY')}</Label>
                      {values.industry ? (
                        <div className='industryView' >
                          <div className='industryCenter'>
                            <span
                              className='industryText'
                              aria-label={financialDetailsData[i18n.language].industryList[values.industry?.value]}
                            >{financialDetailsData[i18n.language].industryList[values.industry?.value]}</span>
                            <IconChevronDown />
                          </div>
                        </div>) :
                        <Select
                          options={[]}
                          placeholder={t(COMMON_SELECT)}
                        />
                      }
                      <FieldFeedback />
                    </Field>}
                    <Heading level={3} marginBottom={2}>{t('ICO.FINANCIAL.COMPANY_TITLE')}</Heading>
                    <Field name="employerName">
                      <Label>{t('ICO.FINANCIAL.EMPLOYERSNAME')}</Label>
                      <TextInput />
                      <FieldFeedback />
                    </Field>
                    <Field name="addressCountry">
                      <Label>{t('ICO.FINANCIAL.COUNTRY')}</Label>
                      <Select
                        options={countryList}
                        placeholder={t(FINANCIAL_SELECTCOUNTRY)}
                      />
                      <FieldFeedback />
                    </Field>
                    <Section>
                      <Heading level={2} accentBar>
                        {t('ICO.FINANCIAL.INCOME')}
                      </Heading>
                      <Field name="gross"
                        onChange={(next, { value, ...rest }) => {
                          value = value.replace(/^0$/g, '');
                          next({ value, ...rest });
                        }}>
                        <Label>{t(employmentStatus === SELF_EMPLOYED ? 'ICO.FINANCIAL.AVERAGE_MONTHLY' : 'ICO.FINANCIAL.HKD')}</Label>
                        <Paragraph hint>{t('ICO.FINANCIAL.AVERAGE_MONTHLY_SUBLABEL')}</Paragraph>
                        <CurrencyInput symbol={t('ICO.FINANCIAL.HKD1')} maxLength={9} />
                        <FieldFeedback />
                      </Field>
                      <Fieldset name="paidFromEmployerIndicator">
                        <Label>{t('ICO.FINANCIAL.PAID_FORM_EMPLOYER')}</Label>
                        <YesNoRadio textMap={{ yes: t('ICO.COMMON.YES'), no: t('ICO.COMMON.NO') }} />
                        <FieldFeedback marginTop={1} />
                      </Fieldset>
                    </Section>
                  </>
                }
                <Section>
                  <Heading level={2} accentBar>
                    {t('ICO.FINANCIAL.TAX')}
                  </Heading>
                  <Expander
                    marginBottom={2}
                    name="notesInfo"
                    startExpanded={false}
                    style={{ maxWidth: '394px' }}
                    textMap={{
                      closeButton: `${t('ICO.FINANCIAL.CLOSE')}`
                    }}
                    title={t('ICO.FINANCIAL.INTO')}
                  >
                    <Paragraph>
                      {t('ICO.FINANCIAL.NATIONAL')}
                      <Link
                        buttonPadding={false}
                        elementType="a"
                        external
                        href={t('ICO.FINANCIAL.LIKE2')}
                        strong={false}
                        textMap={{
                          newWindow: ''
                        }}
                        style={{ wordWrap: 'break-word' }}
                      >
                        &nbsp;{t('ICO.FINANCIAL.LIKE1')}</Link>
                    </Paragraph>
                  </Expander>
                  <AddAnother
                    name="additionalNationalities"
                    max={4}
                    min={1}
                    minOnMount={1}
                    itemInitialValue={{
                      countryOfTaxResidency: '',
                      taxDocumentIdNumber: '',
                      agree: '',
                      reasonForNoTIN: '',
                      explainForNotEligibleApply: '',
                    }}
                    textMap={{
                      addButton: `${t('ICO.FINANCIAL.ADD')}`,
                    }}
                    fullWidth
                    renderAddButtonText={() => t('ICO.FINANCIAL.ADD')}
                    renderRemoveButtonText={() => t('ICO.FINANCIAL.REMOVE')}
                  >
                    {({ path, index }) => {
                      const financialExampleId = `financial_example_${index}`;
                      const financialTinHelpId = `financial_tin_help_${index}`;
                      const isChinaTaxResidency = values.additionalNationalities[index].countryOfTaxResidency === CHINA;
                      const reasonForNoTINError = errors?.additionalNationalities?.[index]?.reasonForNoTIN;
                      const selectedReasonForNoTIN = values.additionalNationalities[index].reasonForNoTIN;
                      const noTinReasonDisplayError = getNoTinReasonDisplayError({ reasonForNoTINError });
                      const noTinReasonDisplayWarning = getNoTinReasonDisplayWarning({ selectedReasonForNoTIN, t });

                      return (
                        <Section marginBottom={0}>
                        <Field
                          name={`${path}.countryOfTaxResidency`}
                          onChange={(next, { value, ...rest }) => {
                            clearTaxValidationErrors(index);
                            if(value === CHINA){
                              values.additionalNationalities[index].agree = '';
                              values.additionalNationalities[index].reasonForNoTIN = '';
                              values.additionalNationalities[index].explainForNotEligibleApply = '';
                            }

                            next({ value, ...rest });
                          }}
                          >
                          <Label>{t('ICO.FINANCIAL.PURPOSES')}</Label>
                          <Paragraph id="financial_jurisdictions" hint>{t('ICO.FINANCIAL.JURISDICTIONS')}</Paragraph>
                          <Select
                            aria-describedby="financial_jurisdictions"
                            options={getTaxResidencyCountryOptions(countryList, values.additionalNationalities, index)}
                            placeholder={t(FINANCIAL_SELECTCOUNTRY)}
                          />
                          <FieldFeedback />
                        </Field>
                        {
                          (!values.additionalNationalities[index].agree ||
                            isChinaTaxResidency) &&
                          <Field name={`${path}.taxDocumentIdNumber`}
                            onChange={(next, { value, ...rest }) => {
                              clearTaxValidationErrors(index, 'taxDocumentIdNumber');
                              next({ value, ...rest });
                            }}>
                            <Label>{t('ICO.FINANCIAL.ENTERTAX')}</Label>
                            {!isChinaTaxResidency && (
                              <Paragraph
                                id={financialExampleId}
                                hint
                              >{t('ICO.FINANCIAL.EXAMPLE')}</Paragraph>
                            )}
                            {isChinaTaxResidency && renderTinHelpText(t, financialTinHelpId)}
                            <TextInput aria-describedby={isChinaTaxResidency ? financialTinHelpId : financialExampleId} />
                            <FieldFeedback />
                          </Field>
                        }
                        {
                          values.additionalNationalities[index].agree &&
                          values.additionalNationalities[index].countryOfTaxResidency !== CHINA &&
                          <Field>
                            <Label>{t('ICO.FINANCIAL.ENTERTAX')}</Label>
                            <Paragraph hint>{t('ICO.FINANCIAL.EXAMPLE')}</Paragraph>
                            <TextInput disabled />
                          </Field>
                        }
                        {
                          !isChinaTaxResidency &&
                          <Field
                            marginBottom={5}
                            name={`${path}.agree`}
                            onChange={(next, { value, ...rest }) => additionalNationalitiesAgreeHandle({ next, value, rest }, { values, index })}
                          >
                            <Checkbox>
                              {t('ICO.FINANCIAL.UNABLETIN')}
                            </Checkbox>
                            <FieldFeedback />
                          </Field>
                        }
                        {
                          values.additionalNationalities[index].agree &&
                          !isChinaTaxResidency &&
                          <Reveal accentBar>
                            <Field name={`${path}.reasonForNoTIN`}
                              onChange={(next, { value, ...rest }) => additionalNationalitiesReasonForNoTINHandle({ next, value, rest }, { values, index })}
                            >
                              <Label>{t('ICO.FINANCIAL.TELL')}</Label>
                              {NOT_TIN_REASON_OPTIONS.map((item) => {
                                const isSelectedReason = selectedReasonForNoTIN === item.value;
                                const selectedReasonError = isSelectedReason ? noTinReasonDisplayError : '';
                                const showWaitingForTinReminder = isSelectedReason && selectedReasonForNoTIN === WAITING_FOR_TIN_REASON_VALUE;

                                return (
                                  <React.Fragment key={item.value}>
                                    <Radio
                                      value={item.value}
                                      invalid={Boolean(selectedReasonError)}
                                    >
                                      {t(item.label)}
                                    </Radio>
                                    {showWaitingForTinReminder && <WarningFeedback>{noTinReasonDisplayWarning}</WarningFeedback>}
                                    {selectedReasonError && <FieldFeedback>{selectedReasonError}</FieldFeedback>}
                                  </React.Fragment>
                                );
                              })}
                              {!selectedReasonForNoTIN && <FieldFeedback />}
                            </Field>
                            {values.additionalNationalities[index].reasonForNoTIN === 'EXPLANATION2' &&
                              < Field name={`${path}.explainForNotEligibleApply`}>
                                <Label>{t('ICO.FINANCIAL.ARRLY')}</Label>
                                <TextInput />
                                <FieldFeedback />
                              </Field>
                            }
                          </Reveal>
                        }
                        </Section>
                      );
                    }}
                  </AddAnother>
                </Section>
                <ButtonWithBack isSubmitDisabled={isTaxValidationLoading} isLoading={isTaxValidationLoading} />
              </>
            );
          }}
        </Form >
      </FormLayout>
      <ModalDialog
        title={t('ICO.FINANCIAL.MODAL_TITLE')}
        getApplicationNode={function () { return document.getElementById('root'); }}
        show={showModal}
        onClose={() => { setShowModal(false); }}
        textMap={{
          closeButtonLabel: t('ICO.FINANCIAL.MODAL_ICON_CLOSE')
        }}
        dismissible>

        <Form
          id='financialModalForm'
          initialValues={initialModalValues}
          validate={({ residentialReason, residentialReasonOther, correspondenceReason, explainResidenceReasonText }) => {
            const errors = {};
            if (showResidentialReason && !residentialReason) {
              errors.residentialReason = t('ICO.FINANCIAL.MODAL_ERROR_MESSAGE');
            }
            if (showResidentialReason && residentialReason === OTHER_REASON_VALUE) {
              const residentialReasonOtherError = validateModalReason(residentialReasonOther, t);
              if (residentialReasonOtherError) {
                errors.residentialReasonOther = residentialReasonOtherError;
              }
            }
            if (showCorrespondenceReason && !correspondenceReason) {
              errors.correspondenceReason = t('ICO.FINANCIAL.MODAL_ERROR_MESSAGE');
            }
            if (showCorrespondenceReason && correspondenceReason === OTHER_REASON_VALUE) {
              const explainResidenceReasonTextError = validateModalReason(explainResidenceReasonText, t);
              if (explainResidenceReasonTextError) {
                errors.explainResidenceReasonText = explainResidenceReasonTextError;
              }
            }
            return errors;
          }}
          onSubmit={({ values }) => {
            const financialData = getUserSessionDataByKey('financial');
            setUserSessionDataByKey('financial', { ...financialData, ...values });
            setShowModal(false);
            history.push('/info/using');

            // submit event tagging
            linkTagging(
              getTaggingProperties(
                showResidentialReason,
                showCorrespondenceReason,
                'submit',
                {
                  event_action: 'button',
                  event_content: `submit`,
                }
              )
            );

          }}
          onChange={rePrevious}
        >
          {(formik) => {
            const { values, setFieldValue } = formik;
            focusSelectDom(formik);
            return (
              <>
                {showResidentialReason && (
                  <Field 
                    name="residentialReason" 
                    onChange={(next, { value, ...rest }) => {
                      linkTagging(
                        getTaggingProperties(
                          showResidentialReason,
                          showCorrespondenceReason,
                          'dropdown1',
                          {
                            event_action: 'dropdown',
                            event_content: getAllValue(residentialReasonList, value),
                          }
                        )
                      );

                      if (value !== OTHER_REASON_VALUE) {
                        setFieldValue('residentialReasonOther', '');
                      }

                      next({ value, ...rest });

                    }}                       
                  >
                    <Label>{t('ICO.FINANCIAL.MODAL_CURRENT_RESIDENTIAL_REASON')}</Label>
                    <Select
                      options={residentialReasonList}
                      placeholder={t('ICO.FINANCIAL.MODAL_PLACE_HOLDER')}                 
                    />
                    <FieldFeedback />
                  </Field>
                )}

                {showResidentialReason && values.residentialReason === OTHER_REASON_VALUE && (
                  <Field name="residentialReasonOther" style={{ maxWidth: '394px' }}>
                    <Label>{t('ICO.FINANCIAL.MODAL_REASON_QUESTION')}</Label>
                    <TextArea minRows={4} disableResize style={{ maxWidth: '394px' }} />
                    <FieldFeedback />
                  </Field>
                )}

                {showCorrespondenceReason && (
                  <Field 
                    name="correspondenceReason"
                    onChange={(next, { value, ...rest }) => {
                      linkTagging(
                        getTaggingProperties(
                          showResidentialReason,
                          showCorrespondenceReason,
                          'dropdown2',
                          {
                            event_action: 'dropdown',
                            event_content: getAllValue(correspondenceReasonList, value),
                          }
                        )
                      );

                      if (value !== OTHER_REASON_VALUE) {
                        setFieldValue('explainResidenceReasonText', '');
                      }

                      next({ value, ...rest });
                    }}                       
                  >
                    <Label>{t('ICO.FINANCIAL.MODAL_CORRESPONDENCE_REASON')}</Label>
                    <Select
                      options={correspondenceReasonList}
                      placeholder={t('ICO.FINANCIAL.MODAL_PLACE_HOLDER')}                   
                    />
                    <FieldFeedback />
                  </Field>
                )}

                {showCorrespondenceReason && values.correspondenceReason === OTHER_REASON_VALUE && (
                  <Field name="explainResidenceReasonText" style={{ maxWidth: '394px' }}>
                    <Label>{t('ICO.FINANCIAL.MODAL_REASON_QUESTION')}</Label>
                    <TextArea minRows={4} disableResize style={{ maxWidth: '394px' }} />
                    <FieldFeedback />
                  </Field>
                )}

                <ButtonRow>
                  <Button type="submit">
                    {t('ICO.FINANCIAL.MODAL_SUBMIT')}
                  </Button>

                  <Button type="button" secondary onClick={() => { 
                      linkTagging(
                        getTaggingProperties(
                          showResidentialReason,
                          showCorrespondenceReason,
                          'cancel',
                          {
                            event_action: 'button',
                            event_content: `cancel`,
                          }
                        )
                      );
                      setShowModal(false); 
                    }}>
                    {t('ICO.FINANCIAL.MODAL_CANCEL')}
                  </Button>
                </ButtonRow>
              </>
            )
          }}
        </Form>

      </ModalDialog>
    </>
  );
}
export default FinancialDetails;
